/**
 * A roster period, and where it ends — the same arithmetic the server does.
 *
 * The server is the source of truth: it recomputes the end from the type, the
 * start and the count on every save, and a date this file produced is never
 * sent. What this is for is the **preview** — somebody choosing "3 monthly
 * periods from 1 June" should see "to 31 August" before they commit, not
 * after.
 *
 * Which means it has to agree with `Modules\Business\Services\SchedulePeriod`
 * exactly, including at the two places naive date maths gets it wrong:
 *
 *  - **No overflow at the end of a month.** 31 January plus one month is
 *    28 February, not 3 March. Doing it with `setMonth` on a Date lets the day
 *    spill into the following month, which would preview three extra days of
 *    roster every January.
 *  - **The end is the last day the roster applies**, so it is the anniversary
 *    **minus a day**: one month from 1 January ends on 31 January, and one
 *    month from 31 January ends on 27 February. Previewing the anniversary
 *    itself would show a period a day longer than the one being saved.
 *
 * Everything here is plain `YYYY-MM-DD` and UTC. A roster's window is
 * branch-local calendar days; pulling a local Date into it would make the
 * preview depend on where the browser is.
 */

export const WEEKLY = "weekly";
export const MONTHLY = "monthly";
export const QUARTERLY = "quarterly";

/** The only three. `always` is deliberately not among them. */
export const PERIOD_TYPES = [WEEKLY, MONTHLY, QUARTERLY];

export const MIN_PERIODS = 1;
export const MAX_PERIODS = 104;

const isDate = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

/** The last day of a given month, so a day number can be clamped to it. */
const lastDayOf = (year, monthIndex) => new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

/**
 * `months` calendar months on, without overflowing into the next one.
 *
 * The day is clamped to the target month's length — which is the whole
 * difference between this and `setMonth`.
 */
const addMonthsNoOverflow = (date, months) => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  const target = new Date(Date.UTC(year, month + months, 1));

  return new Date(
    Date.UTC(
      target.getUTCFullYear(),
      target.getUTCMonth(),
      Math.min(day, lastDayOf(target.getUTCFullYear(), target.getUTCMonth()))
    )
  );
};

/**
 * The last date a roster of `count` periods starting on `startsOn` applies,
 * as `YYYY-MM-DD` — or null when the inputs are not yet a period.
 *
 * Null rather than a guess: a half-filled form has no end date, and showing
 * one derived from a blank count would be inventing an answer.
 */
export const computeEndsOn = (type, startsOn, count) => {
  const n = Number(count);

  if (!PERIOD_TYPES.includes(type) || !isDate(startsOn)) return null;
  if (!Number.isInteger(n) || n < MIN_PERIODS || n > MAX_PERIODS) return null;

  const start = new Date(`${startsOn}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return null;

  let anniversary;

  if (type === WEEKLY) {
    anniversary = new Date(start.getTime());
    anniversary.setUTCDate(anniversary.getUTCDate() + n * 7);
  } else {
    anniversary = addMonthsNoOverflow(start, type === QUARTERLY ? n * 3 : n);
  }

  // Inclusive end — see the note at the top.
  anniversary.setUTCDate(anniversary.getUTCDate() - 1);

  return toIso(anniversary);
};

/** Whether a count is one the server will accept, for enabling the save. */
export const isValidCount = (count) => {
  const n = Number(count);
  return Number.isInteger(n) && n >= MIN_PERIODS && n <= MAX_PERIODS;
};
